/**
 * Account settings: addresses, password, a copy of everything, and the way out.
 *
 * Two decisions here are security decisions rather than layout ones.
 *
 * **A new address does not become your sign-in address until it is confirmed.**
 * allauth would allow promoting an unconfirmed address, because the server is
 * configured with `ACCOUNT_EMAIL_VERIFICATION = optional`. Doing so would mean a
 * single typo — `gmial.com` — permanently locks a learner out of an account
 * whose reset email now goes to an address that does not exist. So the flow is
 * add, confirm, then promote, and the promote control does not appear until the
 * server reports `verified`.
 *
 * **Changing a password ends every session.** The server sets
 * `ACCOUNT_LOGOUT_ON_PASSWORD_CHANGE = True`, which is the correct behaviour
 * after a possible compromise. The screen says so before the learner commits,
 * rather than appearing to log them out unexpectedly.
 *
 * Deletion is genuinely destructive and irreversible, so it asks the learner to
 * type their address — a confirm dialog is muscle memory, and typing is not.
 */

import { useCallback, useEffect, useState } from 'react';
import { Button } from '../design/Button';
import { PasswordField, TextField } from '../design/Field';
import { Alert, Card, Dialog } from '../design/Feedback';
import { passwordRequirements, type EmailAddress } from '../api/auth';
import { FailureSummary, useSubmission } from './formSupport';
import { useAuth } from './AuthProvider';
import { repositories } from '../storage/repositories';
import type { Route } from './routes';

export interface AccountSettingsProps {
  navigate: (route: Route, options?: { replace?: boolean }) => void;
}

export function AccountSettings({ navigate }: AccountSettingsProps) {
  const { auth, api, state, refresh } = useAuth();
  const [addresses, setAddresses] = useState<EmailAddress[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  /**
   * A successful password change ends the session, which would otherwise drop
   * this screen straight into its signed-out fallback — replacing the
   * explanation of what just happened with a bare "You are not signed in". The
   * flag keeps the confirmation on screen through the sign-out it describes.
   */
  const [passwordChanged, setPasswordChanged] = useState(false);

  const loadAddresses = useCallback(async () => {
    try {
      setAddresses(await auth.emailAddresses());
    } catch {
      // Not fatal: the rest of the screen still works, and the addresses
      // section simply shows what the session already knows.
      setAddresses([]);
    }
  }, [auth]);

  useEffect(() => {
    void loadAddresses();
  }, [loadAddresses]);

  if (passwordChanged) {
    return (
      <div className="mm-account">
        <h1 className="mm-account__title">Password changed</h1>
        <Alert tone="success">
          Your password is changed, and every device has been signed out — including this one.
          Your work on this iPad is untouched and still available offline.
        </Alert>
        <div className="mm-auth__actions">
          <Button
            variant="primary"
            size="lg"
            onClick={() => navigate({ name: 'sign-in' }, { replace: true })}
          >
            Sign in
          </Button>
          <Button variant="tertiary" onClick={() => navigate({ name: 'app', destination: 'home' })}>
            Keep writing without signing in
          </Button>
        </div>
      </div>
    );
  }

  if (state.status !== 'signed-in') {
    return (
      <div className="mm-account">
        <h1 className="mm-account__title">Account</h1>
        <Alert tone="info">
          You are not signed in. Your work is saved on this iPad.
        </Alert>
        <div className="mm-auth__actions">
          <Button variant="primary" onClick={() => navigate({ name: 'sign-in' })}>
            Sign in
          </Button>
          <Button variant="tertiary" onClick={() => navigate({ name: 'app', destination: 'home' })}>
            Back to writing
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mm-account">
      <h1 className="mm-account__title">Account</h1>
      <p className="mm-account__subtitle">{state.user?.email}</p>

      {notice ? <Alert tone="success">{notice}</Alert> : null}

      <EmailSection
        addresses={addresses}
        reload={loadAddresses}
        onNotice={setNotice}
      />
      <PasswordSection onChanged={() => setPasswordChanged(true)} />
      <ExportSection />
      <SessionSection onSignedOut={() => navigate({ name: 'app', destination: 'home' }, { replace: true })} refresh={refresh} />
      <DangerSection
        email={state.user?.email ?? ''}
        api={api}
        onDeleted={() => navigate({ name: 'app', destination: 'home' }, { replace: true })}
      />

      <div className="mm-auth__aside">
        <button type="button" className="mm-auth__link" onClick={() => navigate({ name: 'app', destination: 'home' })}>
          Back to writing
        </button>
      </div>
    </div>
  );
}

// --------------------------------------------------------------- email

function EmailSection({
  addresses,
  reload,
  onNotice,
}: {
  addresses: EmailAddress[];
  reload: () => Promise<void>;
  onNotice: (message: string) => void;
}) {
  const { auth } = useAuth();
  const [adding, setAdding] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { busy, failure, submit } = useSubmission();

  const add = async () => {
    const ok = await submit(async () => {
      // Prove it is still the same person before touching the address the
      // account signs in with.
      await auth.confirmPassword(password);
      setPassword('');
      await auth.addEmail(email.trim());
      await reload();
    });
    if (ok) {
      onNotice(`Confirmation link sent to ${email.trim()}. It becomes your sign-in address once confirmed.`);
      setEmail('');
      setAdding(false);
    } else {
      setPassword('');
    }
  };

  const promote = async (address: string) => {
    const ok = await submit(async () => {
      await auth.makeEmailPrimary(address);
      await reload();
    });
    if (ok) onNotice(`${address} is now your sign-in address.`);
  };

  const remove = async (address: string) => {
    const ok = await submit(async () => {
      await auth.removeEmail(address);
      await reload();
    });
    if (ok) onNotice(`${address} removed.`);
  };

  return (
    <Card title="Email addresses">
      <FailureSummary failure={failure} />

      <ul className="mm-account__addresses">
        {addresses.map((address) => (
          <li key={address.email} className="mm-account__address">
            <div>
              <p className="mm-account__address-email">{address.email}</p>
              <p className="mm-account__address-state">
                {address.primary ? 'Signs you in' : 'Additional address'}
                {' · '}
                {address.verified ? 'Confirmed' : 'Awaiting confirmation'}
              </p>
            </div>
            <div className="mm-account__address-actions">
              {/* Promotion is offered only for an address the server has
                  confirmed. An unconfirmed one here would be a lockout. */}
              {!address.primary && address.verified ? (
                <Button variant="secondary" disabled={busy} onClick={() => void promote(address.email)}>
                  Use to sign in
                </Button>
              ) : null}
              {!address.primary ? (
                <Button variant="tertiary" disabled={busy} onClick={() => void remove(address.email)}>
                  Remove
                </Button>
              ) : null}
            </div>
          </li>
        ))}
      </ul>

      {adding ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void add();
          }}
          noValidate
        >
          <TextField
            label="New email"
            type="email"
            value={email}
            autoComplete="email"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            inputMode="email"
            required
            error={failure?.fields.email}
            onChange={(event) => setEmail(event.target.value)}
          />
          <PasswordField
            // Distinct from the Password section's own "Current password" field:
            // two identically labelled password boxes on one screen are
            // ambiguous to a screen reader and to a password manager.
            label="Confirm your current password"
            value={password}
            autoComplete="current-password"
            required
            help="Confirms it is you before your sign-in address can change."
            error={failure?.fields.password}
            onChange={(event) => setPassword(event.target.value)}
          />
          <div className="mm-auth__actions">
            <Button type="submit" variant="primary" loading={busy}>
              Send confirmation
            </Button>
            <Button variant="tertiary" disabled={busy} onClick={() => setAdding(false)}>
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <Button variant="secondary" onClick={() => setAdding(true)}>
          Add an email address
        </Button>
      )}
    </Card>
  );
}

// ------------------------------------------------------------ password

function PasswordSection({ onChanged }: { onChanged: () => void }) {
  const { auth } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const { busy, failure, submit } = useSubmission();

  const change = async () => {
    const ok = await submit(async () => {
      await auth.changePassword(current, next);
    });
    setCurrent('');
    setNext('');
    if (ok) onChanged();
  };

  return (
    <Card title="Password">
      <FailureSummary failure={failure} handledFields={['current_password', 'new_password']} />
      <p className="mm-account__note">
        Changing your password signs you out on every device, including this one.
      </p>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void change();
        }}
        noValidate
      >
        <PasswordField
          label="Current password"
          value={current}
          autoComplete="current-password"
          required
          error={failure?.fields.current_password}
          onChange={(event) => setCurrent(event.target.value)}
        />
        <PasswordField
          label="New password"
          value={next}
          autoComplete="new-password"
          required
          requirements={passwordRequirements(next, '')}
          error={failure?.fields.new_password}
          onChange={(event) => setNext(event.target.value)}
        />
        <div className="mm-auth__actions">
          <Button type="submit" variant="primary" loading={busy}>
            Change password
          </Button>
        </div>
      </form>
    </Card>
  );
}

// -------------------------------------------------------------- export

function ExportSection() {
  const { api } = useAuth();
  const { busy, failure, submit } = useSubmission();

  const download = async () => {
    await submit(async () => {
      const data = await api.get<unknown>('/account/export');
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `musicmentor-account-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    });
  };

  return (
    <Card title="Your data">
      <FailureSummary failure={failure} />
      <p className="mm-account__note">
        A JSON file containing your account details, documents, lesson progress and practice
        history — everything the server holds about you.
      </p>
      <Button variant="secondary" icon="download" loading={busy} onClick={() => void download()}>
        Download a copy
      </Button>
    </Card>
  );
}

// ------------------------------------------------------------ sessions

function SessionSection({
  onSignedOut,
  refresh,
}: {
  onSignedOut: () => void;
  refresh: () => Promise<void>;
}) {
  const { auth } = useAuth();
  const { busy, failure, submit } = useSubmission();

  const out = async (everywhere: boolean) => {
    const ok = await submit(async () => {
      if (everywhere) await auth.signOutEverywhere();
      else await auth.signOut();
      await refresh();
    });
    if (ok) onSignedOut();
  };

  return (
    <Card title="Sessions">
      <FailureSummary failure={failure} />
      <p className="mm-account__note">
        Signing out leaves your work on this iPad. It stays available offline, signed out.
      </p>
      <div className="mm-auth__actions">
        <Button variant="secondary" loading={busy} onClick={() => void out(false)}>
          Sign out
        </Button>
        <Button variant="tertiary" disabled={busy} onClick={() => void out(true)}>
          Sign out everywhere
        </Button>
      </div>
    </Card>
  );
}

// -------------------------------------------------------------- danger

function DangerSection({
  email,
  api,
  onDeleted,
}: {
  email: string;
  api: ReturnType<typeof useAuth>['api'];
  onDeleted: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [alsoLocal, setAlsoLocal] = useState(false);
  const { busy, failure, submit } = useSubmission();

  const confirmed = typed.trim().toLowerCase() === email.trim().toLowerCase();

  const remove = async () => {
    const ok = await submit(async () => {
      await api.delete('/account');
      // Only when explicitly asked. Deleting the server copy does not imply
      // consent to wipe the iPad, and the two are separate promises.
      if (alsoLocal) {
        const repos = repositories();
        for (const document of await repos.documents.list()) {
          await repos.documents.remove(document.id);
        }
      }
    });
    if (ok) {
      setOpen(false);
      onDeleted();
    }
  };

  return (
    <Card title="Delete account">
      <FailureSummary failure={failure} />
      <p className="mm-account__note">
        This permanently deletes your account and everything the server holds. It cannot be undone.
      </p>
      <Button variant="destructive" onClick={() => setOpen(true)}>
        Delete my account
      </Button>

      <Dialog
        open={open}
        title="Delete your account?"
        destructive
        confirmLabel={busy ? 'Deleting…' : 'Delete permanently'}
        cancelLabel="Keep my account"
        onCancel={() => {
          setOpen(false);
          setTyped('');
        }}
        onConfirm={() => {
          if (confirmed && !busy) void remove();
        }}
      >
        <p>
          Everything on the server is deleted: your documents, lesson progress and practice
          history. This cannot be undone.
        </p>
        <TextField
          label={`Type ${email} to confirm`}
          value={typed}
          autoComplete="off"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          onChange={(event) => setTyped(event.target.value)}
        />
        <label className="mm-checkbox" htmlFor="delete-local-too">
          <input
            id="delete-local-too"
            type="checkbox"
            checked={alsoLocal}
            onChange={(event) => setAlsoLocal(event.target.checked)}
          />
          <span className="mm-checkbox__box" aria-hidden="true" />
          <span className="mm-checkbox__label">
            Also remove the copies saved on this iPad
          </span>
        </label>
        {!confirmed ? (
          <p className="mm-account__note" role="status">
            Type your email address above to enable deletion.
          </p>
        ) : null}
      </Dialog>
    </Card>
  );
}
